import {DefinitionReadOnlyOverview} from '@github-ui/custom-properties-core/components/DefinitionReadOnlyOverview'
import {PropertiesBreadcrumbsPageHeader} from '@github-ui/custom-properties-core/components/PropertiesBreadcrumbsPageHeader'
import {PropertyManagementBanner} from '@github-ui/custom-properties-core/components/PropertyManagementBanner'
import {ServerErrorFormBanner as SharedServerErrorFormBanner} from '@github-ui/custom-properties-core/components/ServerErrorFormBanner'
import type {CustomPropertyDetailsPagePayload, PropertyDefinition} from '@github-ui/custom-properties-types'
import {propertyDefinitionSettingsPath} from '@github-ui/paths'
import {Link} from '@github-ui/react-core/link'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {useNavigate} from '@github-ui/use-navigate'
import {PencilIcon} from '@primer/octicons-react'
import {Breadcrumbs, Button} from '@primer/react'
import {useEffect, useRef, useState} from 'react'

import {ServerErrorFormBanner} from '../components/Banners'
import {DefinitionDangerZone} from '../components/DefinitionDangerZone'
import {DefinitionForm} from '../components/DefinitionForm'
import {PropertyPageHeader} from '../components/PageHeader'
import {PropertyDefinitionSettings} from '../components/PropertyDefinitionSettings'
import {PropertyNotManageableBanner} from '../components/PropertyNotManageableBanner'
import {ReadOnlyPropertyDefinitionSettings} from '../components/ReadOnlyPropertyDefinitionSettings'
import {useListPropertiesPath} from '../hooks/use-properties-paths'
import {useEditPropertyPathBuilder} from '../hooks/use-property-details-paths'
import {usePropertySource} from '../hooks/use-property-source'
import styles from './CustomPropertyDetailsPage.module.css'

export function CustomPropertyDetailsPage() {
  const enableSharedComponents = useFeatureFlag('custom_properties_shared_components')
  const {propertyNames, definition, business, canManageProperty, orgConflicts} =
    useRoutePayload<CustomPropertyDetailsPagePayload>()
  const [formError, setFormError] = useState<SafeHTMLString>('' as SafeHTMLString)
  const {settingsLevel} = usePropertySource()
  const [isEditing, setEditing] = useState(!definition)
  const canEdit = !!definition && settingsLevel === definition.source.type

  const navigate = useNavigate()
  const listPropertiesPath = useListPropertiesPath()
  const propertyDetailsPathBuilder = useEditPropertyPathBuilder()

  function returnToList() {
    navigate(listPropertiesPath)
  }

  function onSuccess(propertyName: string) {
    setEditing(false)
    navigate(propertyDetailsPathBuilder(propertyName))
  }

  function onCancel() {
    const isEditingExistingDefinition = !!definition
    if (isEditingExistingDefinition) {
      setEditing(false)
    } else {
      returnToList()
    }
  }

  const isPropNotManagedByThisContext = definition && definition.source.type !== settingsLevel
  const canDeleteProperty = settingsLevel === definition?.source?.type
  const canPromoteProperty = settingsLevel === 'business' && definition?.source.type === 'org'
  const showDangerZone = canDeleteProperty || canPromoteProperty

  const serverErrorBannerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    serverErrorBannerRef.current?.focus()
  }, [formError, serverErrorBannerRef])

  if (enableSharedComponents) {
    return (
      <>
        {formError && (
          <div className={styles.errorContainer}>
            <SharedServerErrorFormBanner ref={serverErrorBannerRef}>
              <VerifiedHTMLText html={formError} />
            </SharedServerErrorFormBanner>
          </div>
        )}
        <PropertiesBreadcrumbsPageHeader
          className="mb-3"
          pageTitle={definition?.propertyName || 'New property'}
          breadcrumbLinkProps={{to: listPropertiesPath}}
          actions={
            !isEditing &&
            canEdit && (
              <Button className="ml-3 f5 text-semibold" onClick={() => setEditing(true)} leadingVisual={PencilIcon}>
                Edit
              </Button>
            )
          }
        />
        {isPropNotManagedByThisContext && (
          <PropertyManagementBanner
            className="mb-3 mt-2"
            definitionSource={definition.source}
            definitionDetailsPath={propertyDefinitionSettingsPath({
              pathPrefix: definition.source.type === 'business' ? 'enterprises' : 'organizations',
              sourceName: definition.source.slug,
              propertyName: definition.propertyName,
            })}
            viewerCanManage={canManageProperty}
          />
        )}
        {definition && !isEditing ? (
          <div className="mt-3">
            <DefinitionReadOnlyOverview>
              <DefinitionReadOnlyOverview.CommonAttributes definition={definition} />
              <DefinitionReadOnlyOverview.DataRow label="Allow repository actors to set this property">
                {definition.valuesEditableBy === 'org_and_repo_actors' ? 'Enabled' : 'Disabled'}
              </DefinitionReadOnlyOverview.DataRow>
            </DefinitionReadOnlyOverview>
            {showDangerZone && (
              <div className="mt-4">
                <DefinitionDangerZone
                  business={business}
                  definition={definition}
                  orgConflicts={orgConflicts}
                  canDelete={canDeleteProperty}
                  canPromote={canPromoteProperty}
                />
              </div>
            )}
          </div>
        ) : (
          <DefinitionForm
            definition={definition}
            existingPropertyNames={propertyNames}
            onCancel={onCancel}
            onSuccess={onSuccess}
            setFormError={setFormError}
          />
        )}
      </>
    )
  }

  return (
    <>
      {formError && (
        <div className={styles.errorContainer}>
          <ServerErrorFormBanner ref={serverErrorBannerRef}>
            <VerifiedHTMLText html={formError} />
          </ServerErrorFormBanner>
        </div>
      )}
      <Header definition={definition} canEdit={canEdit} isEditing={isEditing} onEditClick={() => setEditing(true)} />
      {isPropNotManagedByThisContext && (
        <PropertyNotManageableBanner
          propertySource={definition.source}
          propertyName={definition.propertyName}
          viewerCanManage={canManageProperty}
        />
      )}
      {definition && !isEditing ? (
        <div className="mt-3">
          <ReadOnlyPropertyDefinitionSettings definition={definition} />
          {showDangerZone && (
            <div className="mt-4">
              <DefinitionDangerZone
                business={business}
                orgConflicts={orgConflicts}
                definition={definition}
                canDelete={canDeleteProperty}
                canPromote={canPromoteProperty}
              />
            </div>
          )}
        </div>
      ) : (
        <div className={styles.settingsFormContainer}>
          <PropertyDefinitionSettings
            definition={definition}
            existingPropertyNames={propertyNames}
            onCancel={onCancel}
            onSuccess={onSuccess}
            setFormError={setFormError}
          />
        </div>
      )}
    </>
  )
}

interface HeaderProps {
  definition?: PropertyDefinition
  canEdit: boolean
  isEditing: boolean
  onEditClick(): void
}

function Header({definition, canEdit, isEditing, onEditClick}: HeaderProps) {
  const definitionsListPath = useListPropertiesPath()

  return (
    <>
      <Breadcrumbs className={styles.pageNavigationBreadcrumbs}>
        <Breadcrumbs.Item as={Link} to={definitionsListPath}>
          Custom properties
        </Breadcrumbs.Item>
        <Breadcrumbs.Item selected>{definition?.propertyName || 'New property'}</Breadcrumbs.Item>
      </Breadcrumbs>
      <PropertyPageHeader
        title={<div>{definition?.propertyName || 'New property'}</div>}
        actions={
          definition &&
          canEdit &&
          !isEditing && (
            <Button className="ml-3 f5 text-semibold" onClick={onEditClick} leadingVisual={PencilIcon}>
              Edit
            </Button>
          )
        }
      />
    </>
  )
}

try{ CustomPropertyDetailsPage.displayName ||= 'CustomPropertyDetailsPage' } catch {}
try{ Header.displayName ||= 'Header' } catch {}